import React, { useState, useContext } from 'react';
import { Button, Input, Label, Spinner } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import Icon from '@mdi/react';
import { mdiWindowClose, mdiAccountOutline } from '@mdi/js';
import { fetchHRCodeList } from '../../../../../store/apis/commonService';
import { fetchPickListDetailsAutoComplete } from '../../../../../store/apis/autocompleteService';
import { fetchEmployees } from '../../../../../store/apis/employeeService';
import { EventContext } from '../../../../../pages/Activity/context/EventProvider';
import { CustomSelect, UserAsyncAutocompleteSelect } from '../../../../Common';
import ReportToIcon from '../../../../../assets/icons/report-to.svg';

import './TeamCanvasContent.scss';

const initialValues = {
  departmentId: '',
  reportingToId: '',
};

function TeamCanvasContent({ toggle }) {
  const {
    selectedEmployee,
    handleSelectedEmployee,
    handleDefaultSelectedEmployee,
  } = useContext(EventContext);
  const [employeesData, setEmployeesData] = useState(null);
  const { control, handleSubmit, setValue, reset, watch } = useForm({
    mode: 'onChange',
    defaultValues: initialValues,
  });

  const { data: departmentList } = useQuery({
    queryKey: ['departmentList'],
    queryFn: () => fetchHRCodeList({ type: 'DEPT_ACTIVE' }),
    enabled: true,
    keepPreviousData: true,
  });

  const { mutate: fetchEmployeesMutation, isPending: fetchEmployeesIsLoading } =
    useMutation({
      mutationFn: departmentParams => fetchEmployees(departmentParams),
      onSuccess: data => setEmployeesData(data),
    });

  const onSubmit = values => {
    if (!values.departmentId && !values.reportingToId) {
      return;
    }
    fetchEmployeesMutation({
      departmentId: values.departmentId,
      reportingToId: values.reportingToId?.value,
    });
  };

  const isEmployeeChecked = id => selectedEmployee?.employeeId === id;
  const watchValues = watch();

  let resultsContent;
  if (fetchEmployeesIsLoading) {
    resultsContent = (
      <Spinner className="team-canvas-spinner" color="primary" />
    );
  } else if (employeesData && employeesData.length > 0) {
    resultsContent = employeesData.map(opt => (
      <div
        key={opt.EmpCd.trim()}
        className={`employee-card ${isEmployeeChecked(opt.EmpCd.trim()) ? 'checked' : ''}`}
      >
        <Label
          check
          className="w-100 d-flex flex-row gap-3 p-2 align-items-center"
        >
          <Input
            type="radio"
            name="myRadioGroup"
            value={opt.EmpCd.trim()}
            checked={isEmployeeChecked(opt.EmpCd.trim())}
            onChange={() =>
              handleSelectedEmployee({
                employeeId: opt.EmpCd.trim(),
                employeeName: opt.Name,
              })
            }
          />
          <div className="d-flex flex-column">
            <span>{opt.EmpCd.trim()}</span>
            <span className="fw-bold">{opt.Name}</span>
          </div>
        </Label>
      </div>
    ));
  } else if (!employeesData) {
    resultsContent = null;
  } else {
    resultsContent = (
      <div className="no-employees-message">
        <span>No Employees Found</span>
      </div>
    );
  }

  return (
    <div className="team-canvas-wrapper">
      <div className="team-canvas-header">
        <span className="team-canvas-title">Team Calendar</span>
        <div className="team-canvas-actions">
          <Button
            type="button"
            className="custom-secondary-btn"
            onClick={handleDefaultSelectedEmployee}
          >
            Reset
          </Button>
          <Icon
            path={mdiWindowClose}
            size={1}
            className="close-icon"
            onClick={toggle}
          />
        </div>
      </div>

      <form className="team-canvas-form" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <Label for="employeeSelect" className="mb-2">
            Department
          </Label>
          <Controller
            name="departmentId"
            control={control}
            render={({ field }) => (
              <CustomSelect
                options={departmentList?.map(department => ({
                  value: department?.Code?.trim(),
                  label: department?.Description,
                }))}
                value={field.value}
                onChange={e => {
                  field.onChange(e);
                }}
                placeholder="Select a Department"
                className="external-filter-input"
              />
            )}
          />
        </div>

        <div>
          <Label>Reporting To</Label>
          <Controller
            name="reportingToId"
            control={control}
            render={() => (
              <UserAsyncAutocompleteSelect
                autoCompleteFn={input =>
                  fetchPickListDetailsAutoComplete({
                    prefixText: input,
                    type: 'MANAGER',
                  })
                }
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.Cd,
                    label: item.Name,
                    name: item.Name,
                    code: item.Cd,
                    designation: item.Designation,
                    image: item?.Imagefile,
                  }))
                }
                onChange={e => {
                  setValue('reportingToId', e);
                }}
                placeholder="Search Reporting To"
                isClearable
                isSearchable
                value={watchValues.reportingToId}
                inputIcon={
                  <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                }
                rightKeyAssessor="code"
                defaultShowMenu
                customRightIcon={
                  <span style={{ paddingRight: '10px' }}>
                    <img src={ReportToIcon} alt="report to" />
                  </span>
                }
              />
            )}
          />
        </div>

        <div className="team-canvas-btns">
          <Button
            type="button"
            className="custom-light-btn"
            onClick={() => {
              reset();
              setEmployeesData(null);
            }}
          >
            Clear
          </Button>
          <Button type="submit" className="custom-secondary-btn">
            Search
          </Button>
        </div>
      </form>
      <div className="team-canvas-results">{resultsContent}</div>
    </div>
  );
}

export default TeamCanvasContent;
